import { useQuery, UseQueryResult } from '@tanstack/react-query'

import { CountryDTO } from '@/core/api/dtos/response/misc/country'
import { Country } from '@/core/api/entities/misc/country'
import { CountryMapper } from '@/core/api/mappers/misc/countryMapper'
import miscService from '@/core/api/services/miscService'
import { DEFAULT_PARAMS } from '@/core/hooks/common'

// Get all countries from api
export const useCountries = ({
	config,
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Country[], Error> => {
	const QUERY_KEY = ['countries', 'all']

	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () =>
			miscService.getCountries(config).then(({ data: response }) => {
				return CountryMapper.toEntityList(response?.data || [])
			}),
		...options,
		cacheTime: 86400000, // 24h = 24h * 60m * 60s * 1000ms = 86400000ms
		staleTime: 86400000, // 24h = 24h * 60m * 60s * 1000ms = 86400000ms
	})
}

// Get all countries from local data
export const useOwnCountries = ({
	options,
}: any = DEFAULT_PARAMS): UseQueryResult<Country[], Error> => {
	return useQuery({
		queryKey: ['countries'],
		queryFn: () =>
			new Promise<Country[]>((resolve) => {
				resolve(
					Countries.en.map((country) => {
						return CountryMapper.toEntity(country as CountryDTO)
					})
				)
			}),
		...options,
		cacheTime: 86400000, // 24h = 24h * 60m * 60s * 1000ms = 86400000ms
		staleTime: 86400000, // 24h = 24h * 60m * 60s * 1000ms = 86400000ms
	})
}
const Countries = {
	en: [
		{
			iso: 'AF',
			name: 'Afghanistan',
		},
		{
			iso: 'AX',
			name: 'Åland Islands',
		},
		{
			iso: 'AL',
			name: 'Albania',
		},
		{
			iso: 'DZ',
			name: 'Algeria',
		},
		{
			iso: 'AS',
			name: 'American Samoa',
		},
		{
			iso: 'AD',
			name: 'Andorra',
		},
		{
			iso: 'AO',
			name: 'Angola',
		},
		{
			iso: 'AI',
			name: 'Anguilla',
		},
		{
			iso: 'AQ',
			name: 'Antarctica',
		},
		{
			iso: 'AG',
			name: 'Antigua & Barbuda',
		},
		{
			iso: 'AR',
			name: 'Argentina',
		},
		{
			iso: 'AM',
			name: 'Armenia',
		},
		{
			iso: 'AW',
			name: 'Aruba',
		},
		{
			iso: 'AU',
			name: 'Australia',
		},
		{
			iso: 'AT',
			name: 'Austria',
		},
		{
			iso: 'AZ',
			name: 'Azerbaijan',
		},
		{
			iso: 'BS',
			name: 'Bahamas',
		},
		{
			iso: 'BH',
			name: 'Bahrain',
		},
		{
			iso: 'BD',
			name: 'Bangladesh',
		},
		{
			iso: 'BB',
			name: 'Barbados',
		},
		{
			iso: 'BY',
			name: 'Belarus',
		},
		{
			iso: 'BE',
			name: 'Belgium',
		},
		{
			iso: 'BZ',
			name: 'Belize',
		},
		{
			iso: 'BJ',
			name: 'Benin',
		},
		{
			iso: 'BM',
			name: 'Bermuda',
		},
		{
			iso: 'BT',
			name: 'Bhutan',
		},
		{
			iso: 'BO',
			name: 'Bolivia',
		},
		{
			iso: 'BA',
			name: 'Bosnia & Herzegovina',
		},
		{
			iso: 'BW',
			name: 'Botswana',
		},
		{
			iso: 'BR',
			name: 'Brazil',
		},
		{
			iso: 'IO',
			name: 'British Indian Ocean Territory',
		},
		{
			iso: 'VG',
			name: 'British Virgin Islands',
		},
		{
			iso: 'BN',
			name: 'Brunei',
		},
		{
			iso: 'BG',
			name: 'Bulgaria',
		},
		{
			iso: 'BF',
			name: 'Burkina Faso',
		},
		{
			iso: 'BI',
			name: 'Burundi',
		},
		{
			iso: 'KH',
			name: 'Cambodia',
		},
		{
			iso: 'CM',
			name: 'Cameroon',
		},
		{
			iso: 'CA',
			name: 'Canada',
		},
		{
			iso: 'CV',
			name: 'Cape Verde',
		},
		{
			iso: 'BQ',
			name: 'Caribbean Netherlands',
		},
		{
			iso: 'KY',
			name: 'Cayman Islands',
		},
		{
			iso: 'CF',
			name: 'Central African Republic',
		},
		{
			iso: 'TD',
			name: 'Chad',
		},
		{
			iso: 'CL',
			name: 'Chile',
		},
		{
			iso: 'CN',
			name: 'China',
		},
		{
			iso: 'CX',
			name: 'Christmas Island',
		},
		{
			iso: 'CC',
			name: 'Cocos (Keeling) Islands',
		},
		{
			iso: 'CO',
			name: 'Colombia',
		},
		{
			iso: 'KM',
			name: 'Comoros',
		},
		{
			iso: 'CG',
			name: 'Congo - Brazzaville',
		},
		{
			iso: 'CD',
			name: 'Congo - Kinshasa',
		},
		{
			iso: 'CK',
			name: 'Cook Islands',
		},
		{
			iso: 'CR',
			name: 'Costa Rica',
		},
		{
			iso: 'CI',
			name: 'Côte d’Ivoire',
		},
		{
			iso: 'HR',
			name: 'Croatia',
		},
		{
			iso: 'CU',
			name: 'Cuba',
		},
		{
			iso: 'CW',
			name: 'Curaçao',
		},
		{
			iso: 'CY',
			name: 'Cyprus',
		},
		{
			iso: 'CZ',
			name: 'Czech Republic',
		},
		{
			iso: 'DK',
			name: 'Denmark',
		},
		{
			iso: 'DJ',
			name: 'Djibouti',
		},
		{
			iso: 'DM',
			name: 'Dominica',
		},
		{
			iso: 'DO',
			name: 'Dominican Republic',
		},
		{
			iso: 'EC',
			name: 'Ecuador',
		},
		{
			iso: 'EG',
			name: 'Egypt',
		},
		{
			iso: 'SV',
			name: 'El Salvador',
		},
		{
			iso: 'GQ',
			name: 'Equatorial Guinea',
		},
		{
			iso: 'ER',
			name: 'Eritrea',
		},
		{
			iso: 'EE',
			name: 'Estonia',
		},
		{
			iso: 'ET',
			name: 'Ethiopia',
		},
		{
			iso: 'FK',
			name: 'Falkland Islands',
		},
		{
			iso: 'FO',
			name: 'Faroe Islands',
		},
		{
			iso: 'FJ',
			name: 'Fiji',
		},
		{
			iso: 'FI',
			name: 'Finland',
		},
		{
			iso: 'FR',
			name: 'France',
		},
		{
			iso: 'GF',
			name: 'French Guiana',
		},
		{
			iso: 'PF',
			name: 'French Polynesia',
		},
		{
			iso: 'TF',
			name: 'French Southern Territories',
		},
		{
			iso: 'GA',
			name: 'Gabon',
		},
		{
			iso: 'GM',
			name: 'Gambia',
		},
		{
			iso: 'GE',
			name: 'Georgia',
		},
		{
			iso: 'DE',
			name: 'Germany',
		},
		{
			iso: 'GH',
			name: 'Ghana',
		},
		{
			iso: 'GI',
			name: 'Gibraltar',
		},
		{
			iso: 'GR',
			name: 'Greece',
		},
		{
			iso: 'GL',
			name: 'Greenland',
		},
		{
			iso: 'GD',
			name: 'Grenada',
		},
		{
			iso: 'GP',
			name: 'Guadeloupe',
		},
		{
			iso: 'GU',
			name: 'Guam',
		},
		{
			iso: 'GT',
			name: 'Guatemala',
		},
		{
			iso: 'GG',
			name: 'Guernsey',
		},
		{
			iso: 'GN',
			name: 'Guinea',
		},
		{
			iso: 'GW',
			name: 'Guinea-Bissau',
		},
		{
			iso: 'GY',
			name: 'Guyana',
		},
		{
			iso: 'HT',
			name: 'Haiti',
		},
		{
			iso: 'HN',
			name: 'Honduras',
		},
		{
			iso: 'HU',
			name: 'Hungary',
		},
		{
			iso: 'IS',
			name: 'Iceland',
		},
		{
			iso: 'IN',
			name: 'India',
		},
		{
			iso: 'ID',
			name: 'Indonesia',
		},
		{
			iso: 'IR',
			name: 'Iran',
		},
		{
			iso: 'IQ',
			name: 'Iraq',
		},
		{
			iso: 'IE',
			name: 'Ireland',
		},
		{
			iso: 'IM',
			name: 'Isle of Man',
		},
		{
			iso: 'IL',
			name: 'Israel',
		},
		{
			iso: 'IT',
			name: 'Italy',
		},
		{
			iso: 'JM',
			name: 'Jamaica',
		},
		{
			iso: 'JP',
			name: 'Japan',
		},
		{
			iso: 'JE',
			name: 'Jersey',
		},
		{
			iso: 'JO',
			name: 'Jordan',
		},
		{
			iso: 'KZ',
			name: 'Kazakhstan',
		},
		{
			iso: 'KE',
			name: 'Kenya',
		},
		{
			iso: 'KI',
			name: 'Kiribati',
		},
		{
			iso: 'KW',
			name: 'Kuwait',
		},
		{
			iso: 'KG',
			name: 'Kyrgyzstan',
		},
		{
			iso: 'LA',
			name: 'Laos',
		},
		{
			iso: 'LV',
			name: 'Latvia',
		},
		{
			iso: 'LB',
			name: 'Lebanon',
		},
		{
			iso: 'LS',
			name: 'Lesotho',
		},
		{
			iso: 'LR',
			name: 'Liberia',
		},
		{
			iso: 'LY',
			name: 'Libya',
		},
		{
			iso: 'LI',
			name: 'Liechtenstein',
		},
		{
			iso: 'LT',
			name: 'Lithuania',
		},
		{
			iso: 'LU',
			name: 'Luxembourg',
		},
		{
			iso: 'MO',
			name: 'Macau SAR China',
		},
		{
			iso: 'MK',
			name: 'Macedonia',
		},
		{
			iso: 'MG',
			name: 'Madagascar',
		},
		{
			iso: 'MW',
			name: 'Malawi',
		},
		{
			iso: 'MY',
			name: 'Malaysia',
		},
		{
			iso: 'MV',
			name: 'Maldives',
		},
		{
			iso: 'ML',
			name: 'Mali',
		},
		{
			iso: 'MT',
			name: 'Malta',
		},
		{
			iso: 'MQ',
			name: 'Martinique',
		},
		{
			iso: 'MR',
			name: 'Mauritania',
		},
		{
			iso: 'MU',
			name: 'Mauritius',
		},
		{
			iso: 'YT',
			name: 'Mayotte',
		},
		{
			iso: 'MX',
			name: 'Mexico',
		},
		{
			iso: 'MD',
			name: 'Moldova',
		},
		{
			iso: 'MC',
			name: 'Monaco',
		},
		{
			iso: 'MN',
			name: 'Mongolia',
		},
		{
			iso: 'ME',
			name: 'Montenegro',
		},
		{
			iso: 'MS',
			name: 'Montserrat',
		},
		{
			iso: 'MA',
			name: 'Morocco',
		},
		{
			iso: 'MZ',
			name: 'Mozambique',
		},
		{
			iso: 'MM',
			name: 'Myanmar (Burma)',
		},
		{
			iso: 'NA',
			name: 'Namibia',
		},
		{
			iso: 'NR',
			name: 'Nauru',
		},
		{
			iso: 'NP',
			name: 'Nepal',
		},
		{
			iso: 'NL',
			name: 'Netherlands',
		},
		{
			iso: 'NC',
			name: 'New Caledonia',
		},
		{
			iso: 'NZ',
			name: 'New Zealand',
		},
		{
			iso: 'NI',
			name: 'Nicaragua',
		},
		{
			iso: 'NE',
			name: 'Niger',
		},
		{
			iso: 'NG',
			name: 'Nigeria',
		},
		{
			iso: 'NU',
			name: 'Niue',
		},
		{
			iso: 'NF',
			name: 'Norfolk Island',
		},
		{
			iso: 'KP',
			name: 'North Korea',
		},
		{
			iso: 'NO',
			name: 'Norway',
		},
		{
			iso: 'OM',
			name: 'Oman',
		},
		{
			iso: 'PK',
			name: 'Pakistan',
		},
		{
			iso: 'PS',
			name: 'Palestinian Territories',
		},
		{
			iso: 'PA',
			name: 'Panama',
		},
		{
			iso: 'PG',
			name: 'Papua New Guinea',
		},
		{
			iso: 'PY',
			name: 'Paraguay',
		},
		{
			iso: 'PE',
			name: 'Peru',
		},
		{
			iso: 'PH',
			name: 'Philippines',
		},
		{
			iso: 'PN',
			name: 'Pitcairn Islands',
		},
		{
			iso: 'PL',
			name: 'Poland',
		},
		{
			iso: 'PT',
			name: 'Portugal',
		},
		{
			iso: 'PR',
			name: 'Puerto Rico',
		},
		{
			iso: 'QA',
			name: 'Qatar',
		},
		{
			iso: 'RE',
			name: 'Réunion',
		},
		{
			iso: 'RO',
			name: 'Romania',
		},
		{
			iso: 'RU',
			name: 'Russia',
		},
		{
			iso: 'RW',
			name: 'Rwanda',
		},
		{
			iso: 'WS',
			name: 'Samoa',
		},
		{
			iso: 'SM',
			name: 'San Marino',
		},
		{
			iso: 'ST',
			name: 'São Tomé & Príncipe',
		},
		{
			iso: 'SA',
			name: 'Saudi Arabia',
		},
		{
			iso: 'SN',
			name: 'Senegal',
		},
		{
			iso: 'RS',
			name: 'Serbia',
		},
		{
			iso: 'SC',
			name: 'Seychelles',
		},
		{
			iso: 'SL',
			name: 'Sierra Leone',
		},
		{
			iso: 'SG',
			name: 'Singapore',
		},
		{
			iso: 'SX',
			name: 'Sint Maarten',
		},
		{
			iso: 'SK',
			name: 'Slovakia',
		},
		{
			iso: 'SI',
			name: 'Slovenia',
		},
		{
			iso: 'SB',
			name: 'Solomon Islands',
		},
		{
			iso: 'SO',
			name: 'Somalia',
		},
		{
			iso: 'ZA',
			name: 'South Africa',
		},
		{
			iso: 'GS',
			name: 'South Georgia & South Sandwich Islands',
		},
		{
			iso: 'KR',
			name: 'South Korea',
		},
		{
			iso: 'SS',
			name: 'South Sudan',
		},
		{
			iso: 'ES',
			name: 'Spain',
		},
		{
			iso: 'LK',
			name: 'Sri Lanka',
		},
		{
			iso: 'BL',
			name: 'St. Barthélemy',
		},
		{
			iso: 'SH',
			name: 'St. Helena',
		},
		{
			iso: 'KN',
			name: 'St. Kitts & Nevis',
		},
		{
			iso: 'LC',
			name: 'St. Lucia',
		},
		{
			iso: 'MF',
			name: 'St. Martin',
		},
		{
			iso: 'PM',
			name: 'St. Pierre & Miquelon',
		},
		{
			iso: 'VC',
			name: 'St. Vincent & Grenadines',
		},
		{
			iso: 'SD',
			name: 'Sudan',
		},
		{
			iso: 'SR',
			name: 'Suriname',
		},
		{
			iso: 'SJ',
			name: 'Svalbard & Jan Mayen',
		},
		{
			iso: 'SZ',
			name: 'Swaziland',
		},
		{
			iso: 'SE',
			name: 'Sweden',
		},
		{
			iso: 'CH',
			name: 'Switzerland',
		},
		{
			iso: 'SY',
			name: 'Syria',
		},
		{
			iso: 'TW',
			name: 'Taiwan',
		},
		{
			iso: 'TJ',
			name: 'Tajikistan',
		},
		{
			iso: 'TZ',
			name: 'Tanzania',
		},
		{
			iso: 'TH',
			name: 'Thailand',
		},
		{
			iso: 'TL',
			name: 'Timor-Leste',
		},
		{
			iso: 'TG',
			name: 'Togo',
		},
		{
			iso: 'TK',
			name: 'Tokelau',
		},
		{
			iso: 'TO',
			name: 'Tonga',
		},
		{
			iso: 'TT',
			name: 'Trinidad & Tobago',
		},
		{
			iso: 'TN',
			name: 'Tunisia',
		},
		{
			iso: 'TR',
			name: 'Turkey',
		},
		{
			iso: 'TM',
			name: 'Turkmenistan',
		},
		{
			iso: 'TC',
			name: 'Turks & Caicos Islands',
		},
		{
			iso: 'TV',
			name: 'Tuvalu',
		},
		{
			iso: 'VI',
			name: 'U.S. Virgin Islands',
		},
		{
			iso: 'UG',
			name: 'Uganda',
		},
		{
			iso: 'UA',
			name: 'Ukraine',
		},
		{
			iso: 'AE',
			name: 'United Arab Emirates',
		},
		{
			iso: 'GB',
			name: 'United Kingdom',
		},
		{
			iso: 'US',
			name: 'United States',
		},
		{
			iso: 'UY',
			name: 'Uruguay',
		},
		{
			iso: 'UZ',
			name: 'Uzbekistan',
		},
		{
			iso: 'VU',
			name: 'Vanuatu',
		},
		{
			iso: 'VA',
			name: 'Vatican City',
		},
		{
			iso: 'VE',
			name: 'Venezuela',
		},
		{
			iso: 'VN',
			name: 'Vietnam',
		},
		{
			iso: 'WF',
			name: 'Wallis & Futuna',
		},
		{
			iso: 'EH',
			name: 'Western Sahara',
		},
		{
			iso: 'YE',
			name: 'Yemen',
		},
		{
			iso: 'ZM',
			name: 'Zambia',
		},
		{
			iso: 'ZW',
			name: 'Zimbabwe',
		},
	],
	es: [
		{
			iso: 'AF',
			name: 'Afganistán',
		},
		{
			iso: 'AL',
			name: 'Albania',
		},
		{
			iso: 'DE',
			name: 'Alemania',
		},
		{
			iso: 'AD',
			name: 'Andorra',
		},
		{
			iso: 'AO',
			name: 'Angola',
		},
		{
			iso: 'AI',
			name: 'Anguila',
		},
		{
			iso: 'AQ',
			name: 'Antártida',
		},
		{
			iso: 'AG',
			name: 'Antigua y Barbuda',
		},
		{
			iso: 'SA',
			name: 'Arabia Saudí',
		},
		{
			iso: 'DZ',
			name: 'Argelia',
		},
		{
			iso: 'AR',
			name: 'Argentina',
		},
		{
			iso: 'AM',
			name: 'Armenia',
		},
		{
			iso: 'AW',
			name: 'Aruba',
		},
		{
			iso: 'AU',
			name: 'Australia',
		},
		{
			iso: 'AT',
			name: 'Austria',
		},
		{
			iso: 'AZ',
			name: 'Azerbaiyán',
		},
		{
			iso: 'BS',
			name: 'Bahamas',
		},
		{
			iso: 'BD',
			name: 'Bangladés',
		},
		{
			iso: 'BB',
			name: 'Barbados',
		},
		{
			iso: 'BH',
			name: 'Baréin',
		},
		{
			iso: 'BE',
			name: 'Bélgica',
		},
		{
			iso: 'BZ',
			name: 'Belice',
		},
		{
			iso: 'BJ',
			name: 'Benín',
		},
		{
			iso: 'BM',
			name: 'Bermudas',
		},
		{
			iso: 'BY',
			name: 'Bielorrusia',
		},
		{
			iso: 'BO',
			name: 'Bolivia',
		},
		{
			iso: 'BA',
			name: 'Bosnia-Herzegovina',
		},
		{
			iso: 'BW',
			name: 'Botsuana',
		},
		{
			iso: 'BR',
			name: 'Brasil',
		},
		{
			iso: 'BN',
			name: 'Brunéi',
		},
		{
			iso: 'BG',
			name: 'Bulgaria',
		},
		{
			iso: 'BF',
			name: 'Burkina Faso',
		},
		{
			iso: 'BI',
			name: 'Burundi',
		},
		{
			iso: 'BT',
			name: 'Bután',
		},
		{
			iso: 'CV',
			name: 'Cabo Verde',
		},
		{
			iso: 'KH',
			name: 'Camboya',
		},
		{
			iso: 'CM',
			name: 'Camerún',
		},
		{
			iso: 'CA',
			name: 'Canadá',
		},
		{
			iso: 'BQ',
			name: 'Caribe neerlandés',
		},
		{
			iso: 'QA',
			name: 'Catar',
		},
		{
			iso: 'TD',
			name: 'Chad',
		},
		{
			iso: 'CL',
			name: 'Chile',
		},
		{
			iso: 'CN',
			name: 'China',
		},
		{
			iso: 'CY',
			name: 'Chipre',
		},
		{
			iso: 'VA',
			name: 'Ciudad del Vaticano',
		},
		{
			iso: 'CO',
			name: 'Colombia',
		},
		{
			iso: 'KM',
			name: 'Comoras',
		},
		{
			iso: 'KP',
			name: 'Corea del Norte',
		},
		{
			iso: 'KR',
			name: 'Corea del Sur',
		},
		{
			iso: 'CI',
			name: 'Costa de Marfil',
		},
		{
			iso: 'CR',
			name: 'Costa Rica',
		},
		{
			iso: 'HR',
			name: 'Croacia',
		},
		{
			iso: 'CU',
			name: 'Cuba',
		},
		{
			iso: 'CW',
			name: 'Curazao',
		},
		{
			iso: 'DK',
			name: 'Dinamarca',
		},
		{
			iso: 'DM',
			name: 'Dominica',
		},
		{
			iso: 'EC',
			name: 'Ecuador',
		},
		{
			iso: 'EG',
			name: 'Egipto',
		},
		{
			iso: 'SV',
			name: 'El Salvador',
		},
		{
			iso: 'AE',
			name: 'Emiratos Árabes Unidos',
		},
		{
			iso: 'ER',
			name: 'Eritrea',
		},
		{
			iso: 'SK',
			name: 'Eslovaquia',
		},
		{
			iso: 'SI',
			name: 'Eslovenia',
		},
		{
			iso: 'ES',
			name: 'España',
		},
		{
			iso: 'US',
			name: 'Estados Unidos',
		},
		{
			iso: 'EE',
			name: 'Estonia',
		},
		{
			iso: 'ET',
			name: 'Etiopía',
		},
		{
			iso: 'PH',
			name: 'Filipinas',
		},
		{
			iso: 'FI',
			name: 'Finlandia',
		},
		{
			iso: 'FJ',
			name: 'Fiyi',
		},
		{
			iso: 'FR',
			name: 'Francia',
		},
		{
			iso: 'GA',
			name: 'Gabón',
		},
		{
			iso: 'GM',
			name: 'Gambia',
		},
		{
			iso: 'GE',
			name: 'Georgia',
		},
		{
			iso: 'GH',
			name: 'Ghana',
		},
		{
			iso: 'GI',
			name: 'Gibraltar',
		},
		{
			iso: 'GD',
			name: 'Granada',
		},
		{
			iso: 'GR',
			name: 'Grecia',
		},
		{
			iso: 'GL',
			name: 'Groenlandia',
		},
		{
			iso: 'GP',
			name: 'Guadalupe',
		},
		{
			iso: 'GU',
			name: 'Guam',
		},
		{
			iso: 'GT',
			name: 'Guatemala',
		},
		{
			iso: 'GF',
			name: 'Guayana Francesa',
		},
		{
			iso: 'GG',
			name: 'Guernesey',
		},
		{
			iso: 'GN',
			name: 'Guinea',
		},
		{
			iso: 'GQ',
			name: 'Guinea Ecuatorial',
		},
		{
			iso: 'GW',
			name: 'Guinea-Bisáu',
		},
		{
			iso: 'GY',
			name: 'Guyana',
		},
		{
			iso: 'HT',
			name: 'Haití',
		},
		{
			iso: 'HN',
			name: 'Honduras',
		},
		{
			iso: 'HU',
			name: 'Hungría',
		},
		{
			iso: 'IN',
			name: 'India',
		},
		{
			iso: 'ID',
			name: 'Indonesia',
		},
		{
			iso: 'IR',
			name: 'Irán',
		},
		{
			iso: 'IQ',
			name: 'Iraq',
		},
		{
			iso: 'IE',
			name: 'Irlanda',
		},
		{
			iso: 'CX',
			name: 'Isla Christmas',
		},
		{
			iso: 'IM',
			name: 'Isla de Man',
		},
		{
			iso: 'NU',
			name: 'Isla Niue',
		},
		{
			iso: 'NF',
			name: 'Isla Norfolk',
		},
		{
			iso: 'IS',
			name: 'Islandia',
		},
		{
			iso: 'AX',
			name: 'Islas Åland',
		},
		{
			iso: 'KY',
			name: 'Islas Caimán',
		},
		{
			iso: 'CC',
			name: 'Islas Cocos',
		},
		{
			iso: 'CK',
			name: 'Islas Cook',
		},
		{
			iso: 'FO',
			name: 'Islas Feroe',
		},
		{
			iso: 'GS',
			name: 'Islas Georgia del Sur y Sandwich del Sur',
		},
		{
			iso: 'FK',
			name: 'Islas Malvinas',
		},
		{
			iso: 'PN',
			name: 'Islas Pitcairn',
		},
		{
			iso: 'SB',
			name: 'Islas Salomón',
		},
		{
			iso: 'TC',
			name: 'Islas Turcas y Caicos',
		},
		{
			iso: 'VG',
			name: 'Islas Vírgenes Británicas',
		},
		{
			iso: 'VI',
			name: 'Islas Vírgenes de EE. UU.',
		},
		{
			iso: 'IL',
			name: 'Israel',
		},
		{
			iso: 'IT',
			name: 'Italia',
		},
		{
			iso: 'JM',
			name: 'Jamaica',
		},
		{
			iso: 'JP',
			name: 'Japón',
		},
		{
			iso: 'JE',
			name: 'Jersey',
		},
		{
			iso: 'JO',
			name: 'Jordania',
		},
		{
			iso: 'KZ',
			name: 'Kazajistán',
		},
		{
			iso: 'KE',
			name: 'Kenia',
		},
		{
			iso: 'KG',
			name: 'Kirguistán',
		},
		{
			iso: 'KI',
			name: 'Kiribati',
		},
		{
			iso: 'KW',
			name: 'Kuwait',
		},
		{
			iso: 'LA',
			name: 'Laos',
		},
		{
			iso: 'LS',
			name: 'Lesoto',
		},
		{
			iso: 'LV',
			name: 'Letonia',
		},
		{
			iso: 'LB',
			name: 'Líbano',
		},
		{
			iso: 'LR',
			name: 'Liberia',
		},
		{
			iso: 'LY',
			name: 'Libia',
		},
		{
			iso: 'LI',
			name: 'Liechtenstein',
		},
		{
			iso: 'LT',
			name: 'Lituania',
		},
		{
			iso: 'LU',
			name: 'Luxemburgo',
		},
		{
			iso: 'MK',
			name: 'Macedonia',
		},
		{
			iso: 'MG',
			name: 'Madagascar',
		},
		{
			iso: 'MY',
			name: 'Malasia',
		},
		{
			iso: 'MW',
			name: 'Malaui',
		},
		{
			iso: 'MV',
			name: 'Maldivas',
		},
		{
			iso: 'ML',
			name: 'Mali',
		},
		{
			iso: 'MT',
			name: 'Malta',
		},
		{
			iso: 'MA',
			name: 'Marruecos',
		},
		{
			iso: 'MQ',
			name: 'Martinica',
		},
		{
			iso: 'MU',
			name: 'Mauricio',
		},
		{
			iso: 'MR',
			name: 'Mauritania',
		},
		{
			iso: 'YT',
			name: 'Mayotte',
		},
		{
			iso: 'MX',
			name: 'México',
		},
		{
			iso: 'MD',
			name: 'Moldavia',
		},
		{
			iso: 'MC',
			name: 'Mónaco',
		},
		{
			iso: 'MN',
			name: 'Mongolia',
		},
		{
			iso: 'ME',
			name: 'Montenegro',
		},
		{
			iso: 'MS',
			name: 'Montserrat',
		},
		{
			iso: 'MZ',
			name: 'Mozambique',
		},
		{
			iso: 'MM',
			name: 'Myanmar (Birmania)',
		},
		{
			iso: 'NA',
			name: 'Namibia',
		},
		{
			iso: 'NR',
			name: 'Nauru',
		},
		{
			iso: 'NP',
			name: 'Nepal',
		},
		{
			iso: 'NI',
			name: 'Nicaragua',
		},
		{
			iso: 'NE',
			name: 'Níger',
		},
		{
			iso: 'NG',
			name: 'Nigeria',
		},
		{
			iso: 'NO',
			name: 'Noruega',
		},
		{
			iso: 'NC',
			name: 'Nueva Caledonia',
		},
		{
			iso: 'NZ',
			name: 'Nueva Zelanda',
		},
		{
			iso: 'OM',
			name: 'Omán',
		},
		{
			iso: 'NL',
			name: 'Países Bajos',
		},
		{
			iso: 'PK',
			name: 'Pakistán',
		},
		{
			iso: 'PA',
			name: 'Panamá',
		},
		{
			iso: 'PG',
			name: 'Papúa Nueva Guinea',
		},
		{
			iso: 'PY',
			name: 'Paraguay',
		},
		{
			iso: 'PE',
			name: 'Perú',
		},
		{
			iso: 'PF',
			name: 'Polinesia Francesa',
		},
		{
			iso: 'PL',
			name: 'Polonia',
		},
		{
			iso: 'PT',
			name: 'Portugal',
		},
		{
			iso: 'PR',
			name: 'Puerto Rico',
		},
		{
			iso: 'MO',
			name: 'RAE de Macao (China)',
		},
		{
			iso: 'GB',
			name: 'Reino Unido',
		},
		{
			iso: 'CF',
			name: 'República Centroafricana',
		},
		{
			iso: 'CZ',
			name: 'República Checa',
		},
		{
			iso: 'CG',
			name: 'República del Congo',
		},
		{
			iso: 'CD',
			name: 'República Democrática del Congo',
		},
		{
			iso: 'DO',
			name: 'República Dominicana',
		},
		{
			iso: 'RE',
			name: 'Reunión',
		},
		{
			iso: 'RW',
			name: 'Ruanda',
		},
		{
			iso: 'RO',
			name: 'Rumanía',
		},
		{
			iso: 'RU',
			name: 'Rusia',
		},
		{
			iso: 'EH',
			name: 'Sáhara Occidental',
		},
		{
			iso: 'WS',
			name: 'Samoa',
		},
		{
			iso: 'AS',
			name: 'Samoa Americana',
		},
		{
			iso: 'BL',
			name: 'San Bartolomé',
		},
		{
			iso: 'KN',
			name: 'San Cristóbal y Nieves',
		},
		{
			iso: 'SM',
			name: 'San Marino',
		},
		{
			iso: 'MF',
			name: 'San Martín',
		},
		{
			iso: 'PM',
			name: 'San Pedro y Miquelón',
		},
		{
			iso: 'VC',
			name: 'San Vicente y las Granadinas',
		},
		{
			iso: 'SH',
			name: 'Santa Elena',
		},
		{
			iso: 'LC',
			name: 'Santa Lucía',
		},
		{
			iso: 'ST',
			name: 'Santo Tomé y Príncipe',
		},
		{
			iso: 'SN',
			name: 'Senegal',
		},
		{
			iso: 'RS',
			name: 'Serbia',
		},
		{
			iso: 'SC',
			name: 'Seychelles',
		},
		{
			iso: 'SL',
			name: 'Sierra Leona',
		},
		{
			iso: 'SG',
			name: 'Singapur',
		},
		{
			iso: 'SX',
			name: 'Sint Maarten',
		},
		{
			iso: 'SY',
			name: 'Siria',
		},
		{
			iso: 'SO',
			name: 'Somalia',
		},
		{
			iso: 'LK',
			name: 'Sri Lanka',
		},
		{
			iso: 'SZ',
			name: 'Suazilandia',
		},
		{
			iso: 'ZA',
			name: 'Sudáfrica',
		},
		{
			iso: 'SD',
			name: 'Sudán',
		},
		{
			iso: 'SS',
			name: 'Sudán del Sur',
		},
		{
			iso: 'SE',
			name: 'Suecia',
		},
		{
			iso: 'CH',
			name: 'Suiza',
		},
		{
			iso: 'SR',
			name: 'Surinam',
		},
		{
			iso: 'SJ',
			name: 'Svalbard y Jan Mayen',
		},
		{
			iso: 'TH',
			name: 'Tailandia',
		},
		{
			iso: 'TW',
			name: 'Taiwán',
		},
		{
			iso: 'TZ',
			name: 'Tanzania',
		},
		{
			iso: 'TJ',
			name: 'Tayikistán',
		},
		{
			iso: 'IO',
			name: 'Territorio Británico del Océano Índico',
		},
		{
			iso: 'TF',
			name: 'Territorios Australes Franceses',
		},
		{
			iso: 'PS',
			name: 'Territorios Palestinos',
		},
		{
			iso: 'TL',
			name: 'Timor Oriental',
		},
		{
			iso: 'TG',
			name: 'Togo',
		},
		{
			iso: 'TK',
			name: 'Tokelau',
		},
		{
			iso: 'TO',
			name: 'Tonga',
		},
		{
			iso: 'TT',
			name: 'Trinidad y Tobago',
		},
		{
			iso: 'TN',
			name: 'Túnez',
		},
		{
			iso: 'TM',
			name: 'Turkmenistán',
		},
		{
			iso: 'TR',
			name: 'Turquía',
		},
		{
			iso: 'TV',
			name: 'Tuvalu',
		},
		{
			iso: 'UA',
			name: 'Ucrania',
		},
		{
			iso: 'UG',
			name: 'Uganda',
		},
		{
			iso: 'UY',
			name: 'Uruguay',
		},
		{
			iso: 'UZ',
			name: 'Uzbekistán',
		},
		{
			iso: 'VU',
			name: 'Vanuatu',
		},
		{
			iso: 'VE',
			name: 'Venezuela',
		},
		{
			iso: 'VN',
			name: 'Vietnam',
		},
		{
			iso: 'WF',
			name: 'Wallis y Futuna',
		},
		{
			iso: 'YE',
			name: 'Yemen',
		},
		{
			iso: 'DJ',
			name: 'Yibuti',
		},
		{
			iso: 'ZM',
			name: 'Zambia',
		},
		{
			iso: 'ZW',
			name: 'Zimbabue',
		},
	],
	pt: [
		{
			iso: 'AF',
			name: 'Afeganistão',
		},
		{
			iso: 'ZA',
			name: 'África do Sul',
		},
		{
			iso: 'AL',
			name: 'Albânia',
		},
		{
			iso: 'DE',
			name: 'Alemanha',
		},
		{
			iso: 'AD',
			name: 'Andorra',
		},
		{
			iso: 'AO',
			name: 'Angola',
		},
		{
			iso: 'AI',
			name: 'Anguilla',
		},
		{
			iso: 'AQ',
			name: 'Antártida',
		},
		{
			iso: 'AG',
			name: 'Antígua e Barbuda',
		},
		{
			iso: 'SA',
			name: 'Arábia Saudita',
		},
		{
			iso: 'DZ',
			name: 'Argélia',
		},
		{
			iso: 'AR',
			name: 'Argentina',
		},
		{
			iso: 'AM',
			name: 'Armênia',
		},
		{
			iso: 'AW',
			name: 'Aruba',
		},
		{
			iso: 'AU',
			name: 'Austrália',
		},
		{
			iso: 'AT',
			name: 'Áustria',
		},
		{
			iso: 'AZ',
			name: 'Azerbaijão',
		},
		{
			iso: 'BS',
			name: 'Bahamas',
		},
		{
			iso: 'BH',
			name: 'Bahrein',
		},
		{
			iso: 'BD',
			name: 'Bangladesh',
		},
		{
			iso: 'BB',
			name: 'Barbados',
		},
		{
			iso: 'BE',
			name: 'Bélgica',
		},
		{
			iso: 'BZ',
			name: 'Belize',
		},
		{
			iso: 'BJ',
			name: 'Benin',
		},
		{
			iso: 'BM',
			name: 'Bermudas',
		},
		{
			iso: 'BY',
			name: 'Bielorrússia',
		},
		{
			iso: 'BO',
			name: 'Bolívia',
		},
		{
			iso: 'BA',
			name: 'Bósnia e Herzegovina',
		},
		{
			iso: 'BW',
			name: 'Botsuana',
		},
		{
			iso: 'BR',
			name: 'Brasil',
		},
		{
			iso: 'BN',
			name: 'Brunei',
		},
		{
			iso: 'BG',
			name: 'Bulgária',
		},
		{
			iso: 'BF',
			name: 'Burquina Faso',
		},
		{
			iso: 'BI',
			name: 'Burundi',
		},
		{
			iso: 'BT',
			name: 'Butão',
		},
		{
			iso: 'CV',
			name: 'Cabo Verde',
		},
		{
			iso: 'KH',
			name: 'Camboja',
		},
		{
			iso: 'CA',
			name: 'Canadá',
		},
		{
			iso: 'QA',
			name: 'Catar',
		},
		{
			iso: 'KZ',
			name: 'Cazaquistão',
		},
		{
			iso: 'TD',
			name: 'Chade',
		},
		{
			iso: 'CL',
			name: 'Chile',
		},
		{
			iso: 'CN',
			name: 'China',
		},
		{
			iso: 'CY',
			name: 'Chipre',
		},
		{
			iso: 'VA',
			name: 'Cidade do Vaticano',
		},
		{
			iso: 'SG',
			name: 'Cingapura',
		},
		{
			iso: 'CO',
			name: 'Colômbia',
		},
		{
			iso: 'KM',
			name: 'Comores',
		},
		{
			iso: 'CG',
			name: 'Congo - Brazzaville',
		},
		{
			iso: 'CD',
			name: 'Congo - Kinshasa',
		},
		{
			iso: 'KP',
			name: 'Coreia do Norte',
		},
		{
			iso: 'KR',
			name: 'Coreia do Sul',
		},
		{
			iso: 'CI',
			name: 'Costa do Marfim',
		},
		{
			iso: 'CR',
			name: 'Costa Rica',
		},
		{
			iso: 'HR',
			name: 'Croácia',
		},
		{
			iso: 'CU',
			name: 'Cuba',
		},
		{
			iso: 'CW',
			name: 'Curaçao',
		},
		{
			iso: 'DK',
			name: 'Dinamarca',
		},
		{
			iso: 'DJ',
			name: 'Djibuti',
		},
		{
			iso: 'DM',
			name: 'Dominica',
		},
		{
			iso: 'EG',
			name: 'Egito',
		},
		{
			iso: 'SV',
			name: 'El Salvador',
		},
		{
			iso: 'AE',
			name: 'Emirados Árabes Unidos',
		},
		{
			iso: 'EC',
			name: 'Equador',
		},
		{
			iso: 'ER',
			name: 'Eritreia',
		},
		{
			iso: 'SK',
			name: 'Eslováquia',
		},
		{
			iso: 'SI',
			name: 'Eslovênia',
		},
		{
			iso: 'ES',
			name: 'Espanha',
		},
		{
			iso: 'US',
			name: 'Estados Unidos',
		},
		{
			iso: 'EE',
			name: 'Estônia',
		},
		{
			iso: 'ET',
			name: 'Etiópia',
		},
		{
			iso: 'FJ',
			name: 'Fiji',
		},
		{
			iso: 'PH',
			name: 'Filipinas',
		},
		{
			iso: 'FI',
			name: 'Finlândia',
		},
		{
			iso: 'FR',
			name: 'França',
		},
		{
			iso: 'GA',
			name: 'Gabão',
		},
		{
			iso: 'GM',
			name: 'Gâmbia',
		},
		{
			iso: 'GH',
			name: 'Gana',
		},
		{
			iso: 'GE',
			name: 'Geórgia',
		},
		{
			iso: 'GS',
			name: 'Geórgia do Sul e Ilhas Sandwich do Sul',
		},
		{
			iso: 'GI',
			name: 'Gibraltar',
		},
		{
			iso: 'GD',
			name: 'Granada',
		},
		{
			iso: 'GR',
			name: 'Grécia',
		},
		{
			iso: 'GL',
			name: 'Groenlândia',
		},
		{
			iso: 'GP',
			name: 'Guadalupe',
		},
		{
			iso: 'GU',
			name: 'Guam',
		},
		{
			iso: 'GT',
			name: 'Guatemala',
		},
		{
			iso: 'GG',
			name: 'Guernsey',
		},
		{
			iso: 'GY',
			name: 'Guiana',
		},
		{
			iso: 'GF',
			name: 'Guiana Francesa',
		},
		{
			iso: 'GN',
			name: 'Guiné',
		},
		{
			iso: 'GW',
			name: 'Guiné Bissau',
		},
		{
			iso: 'GQ',
			name: 'Guiné Equatorial',
		},
		{
			iso: 'HT',
			name: 'Haiti',
		},
		{
			iso: 'NL',
			name: 'Holanda',
		},
		{
			iso: 'HN',
			name: 'Honduras',
		},
		{
			iso: 'HU',
			name: 'Hungria',
		},
		{
			iso: 'YE',
			name: 'Iêmen',
		},
		{
			iso: 'CX',
			name: 'Ilha Christmas',
		},
		{
			iso: 'IM',
			name: 'Ilha de Man',
		},
		{
			iso: 'NF',
			name: 'Ilha Norfolk',
		},
		{
			iso: 'AX',
			name: 'Ilhas Åland',
		},
		{
			iso: 'KY',
			name: 'Ilhas Caiman',
		},
		{
			iso: 'CC',
			name: 'Ilhas Cocos (Keeling)',
		},
		{
			iso: 'CK',
			name: 'Ilhas Cook',
		},
		{
			iso: 'FO',
			name: 'Ilhas Faroe',
		},
		{
			iso: 'FK',
			name: 'Ilhas Malvinas',
		},
		{
			iso: 'PN',
			name: 'Ilhas Pitcairn',
		},
		{
			iso: 'SB',
			name: 'Ilhas Salomão',
		},
		{
			iso: 'TC',
			name: 'Ilhas Turks e Caicos',
		},
		{
			iso: 'VG',
			name: 'Ilhas Virgens Britânicas',
		},
		{
			iso: 'VI',
			name: 'Ilhas Virgens dos EUA',
		},
		{
			iso: 'IN',
			name: 'Índia',
		},
		{
			iso: 'ID',
			name: 'Indonésia',
		},
		{
			iso: 'IR',
			name: 'Irã',
		},
		{
			iso: 'IQ',
			name: 'Iraque',
		},
		{
			iso: 'IE',
			name: 'Irlanda',
		},
		{
			iso: 'IS',
			name: 'Islândia',
		},
		{
			iso: 'IL',
			name: 'Israel',
		},
		{
			iso: 'IT',
			name: 'Itália',
		},
		{
			iso: 'JM',
			name: 'Jamaica',
		},
		{
			iso: 'JP',
			name: 'Japão',
		},
		{
			iso: 'JE',
			name: 'Jersey',
		},
		{
			iso: 'JO',
			name: 'Jordânia',
		},
		{
			iso: 'KW',
			name: 'Kuwait',
		},
		{
			iso: 'LA',
			name: 'Laos',
		},
		{
			iso: 'LS',
			name: 'Lesoto',
		},
		{
			iso: 'LV',
			name: 'Letônia',
		},
		{
			iso: 'LB',
			name: 'Líbano',
		},
		{
			iso: 'LR',
			name: 'Libéria',
		},
		{
			iso: 'LY',
			name: 'Líbia',
		},
		{
			iso: 'LI',
			name: 'Liechtenstein',
		},
		{
			iso: 'LT',
			name: 'Lituânia',
		},
		{
			iso: 'LU',
			name: 'Luxemburgo',
		},
		{
			iso: 'MO',
			name: 'Macau, RAE da China',
		},
		{
			iso: 'MK',
			name: 'Macedônia',
		},
		{
			iso: 'MG',
			name: 'Madagascar',
		},
		{
			iso: 'MY',
			name: 'Malásia',
		},
		{
			iso: 'MW',
			name: 'Malawi',
		},
		{
			iso: 'MV',
			name: 'Maldivas',
		},
		{
			iso: 'ML',
			name: 'Mali',
		},
		{
			iso: 'MT',
			name: 'Malta',
		},
		{
			iso: 'MA',
			name: 'Marrocos',
		},
		{
			iso: 'MQ',
			name: 'Martinica',
		},
		{
			iso: 'MU',
			name: 'Maurício',
		},
		{
			iso: 'MR',
			name: 'Mauritânia',
		},
		{
			iso: 'YT',
			name: 'Mayotte',
		},
		{
			iso: 'MX',
			name: 'México',
		},
		{
			iso: 'MM',
			name: 'Mianmar (Birmânia)',
		},
		{
			iso: 'MZ',
			name: 'Moçambique',
		},
		{
			iso: 'MD',
			name: 'Moldávia',
		},
		{
			iso: 'MC',
			name: 'Mônaco',
		},
		{
			iso: 'MN',
			name: 'Mongólia',
		},
		{
			iso: 'ME',
			name: 'Montenegro',
		},
		{
			iso: 'MS',
			name: 'Montserrat',
		},
		{
			iso: 'NA',
			name: 'Namíbia',
		},
		{
			iso: 'NR',
			name: 'Nauru',
		},
		{
			iso: 'NP',
			name: 'Nepal',
		},
		{
			iso: 'NI',
			name: 'Nicarágua',
		},
		{
			iso: 'NE',
			name: 'Níger',
		},
		{
			iso: 'NG',
			name: 'Nigéria',
		},
		{
			iso: 'NU',
			name: 'Niue',
		},
		{
			iso: 'NO',
			name: 'Noruega',
		},
		{
			iso: 'NC',
			name: 'Nova Caledônia',
		},
		{
			iso: 'NZ',
			name: 'Nova Zelândia',
		},
		{
			iso: 'OM',
			name: 'Omã',
		},
		{
			iso: 'BQ',
			name: 'Países Baixos Caribenhos',
		},
		{
			iso: 'PA',
			name: 'Panamá',
		},
		{
			iso: 'PG',
			name: 'Papua-Nova Guiné',
		},
		{
			iso: 'PK',
			name: 'Paquistão',
		},
		{
			iso: 'PY',
			name: 'Paraguai',
		},
		{
			iso: 'PE',
			name: 'Peru',
		},
		{
			iso: 'PF',
			name: 'Polinésia Francesa',
		},
		{
			iso: 'PL',
			name: 'Polônia',
		},
		{
			iso: 'PR',
			name: 'Porto Rico',
		},
		{
			iso: 'PT',
			name: 'Portugal',
		},
		{
			iso: 'KE',
			name: 'Quênia',
		},
		{
			iso: 'KG',
			name: 'Quirguistão',
		},
		{
			iso: 'KI',
			name: 'Quiribati',
		},
		{
			iso: 'GB',
			name: 'Reino Unido',
		},
		{
			iso: 'CF',
			name: 'República Centro-Africana',
		},
		{
			iso: 'DO',
			name: 'República Dominicana',
		},
		{
			iso: 'CM',
			name: 'República dos Camarões',
		},
		{
			iso: 'CZ',
			name: 'República Tcheca',
		},
		{
			iso: 'RE',
			name: 'Reunião',
		},
		{
			iso: 'RO',
			name: 'Romênia',
		},
		{
			iso: 'RW',
			name: 'Ruanda',
		},
		{
			iso: 'RU',
			name: 'Rússia',
		},
		{
			iso: 'EH',
			name: 'Saara Ocidental',
		},
		{
			iso: 'PM',
			name: 'Saint Pierre e Miquelon',
		},
		{
			iso: 'WS',
			name: 'Samoa',
		},
		{
			iso: 'AS',
			name: 'Samoa Americana',
		},
		{
			iso: 'SM',
			name: 'San Marino',
		},
		{
			iso: 'SH',
			name: 'Santa Helena',
		},
		{
			iso: 'LC',
			name: 'Santa Lúcia',
		},
		{
			iso: 'BL',
			name: 'São Bartolomeu',
		},
		{
			iso: 'KN',
			name: 'São Cristóvão e Nevis',
		},
		{
			iso: 'MF',
			name: 'São Martinho',
		},
		{
			iso: 'ST',
			name: 'São Tomé e Príncipe',
		},
		{
			iso: 'VC',
			name: 'São Vicente e Granadinas',
		},
		{
			iso: 'SN',
			name: 'Senegal',
		},
		{
			iso: 'SL',
			name: 'Serra Leoa',
		},
		{
			iso: 'RS',
			name: 'Sérvia',
		},
		{
			iso: 'SC',
			name: 'Seychelles',
		},
		{
			iso: 'SX',
			name: 'Sint Maarten',
		},
		{
			iso: 'SY',
			name: 'Síria',
		},
		{
			iso: 'SO',
			name: 'Somália',
		},
		{
			iso: 'LK',
			name: 'Sri Lanka',
		},
		{
			iso: 'SZ',
			name: 'Suazilândia',
		},
		{
			iso: 'SD',
			name: 'Sudão',
		},
		{
			iso: 'SS',
			name: 'Sudão do Sul',
		},
		{
			iso: 'SE',
			name: 'Suécia',
		},
		{
			iso: 'CH',
			name: 'Suíça',
		},
		{
			iso: 'SR',
			name: 'Suriname',
		},
		{
			iso: 'SJ',
			name: 'Svalbard e Jan Mayen',
		},
		{
			iso: 'TH',
			name: 'Tailândia',
		},
		{
			iso: 'TW',
			name: 'Taiwan',
		},
		{
			iso: 'TJ',
			name: 'Tajiquistão',
		},
		{
			iso: 'TZ',
			name: 'Tanzânia',
		},
		{
			iso: 'IO',
			name: 'Território Britânico do Oceano Índico',
		},
		{
			iso: 'TF',
			name: 'Territórios Franceses do Sul',
		},
		{
			iso: 'PS',
			name: 'Territórios palestinos',
		},
		{
			iso: 'TL',
			name: 'Timor-Leste',
		},
		{
			iso: 'TG',
			name: 'Togo',
		},
		{
			iso: 'TK',
			name: 'Tokelau',
		},
		{
			iso: 'TO',
			name: 'Tonga',
		},
		{
			iso: 'TT',
			name: 'Trinidad e Tobago',
		},
		{
			iso: 'TN',
			name: 'Tunísia',
		},
		{
			iso: 'TM',
			name: 'Turcomenistão',
		},
		{
			iso: 'TR',
			name: 'Turquia',
		},
		{
			iso: 'TV',
			name: 'Tuvalu',
		},
		{
			iso: 'UA',
			name: 'Ucrânia',
		},
		{
			iso: 'UG',
			name: 'Uganda',
		},
		{
			iso: 'UY',
			name: 'Uruguai',
		},
		{
			iso: 'UZ',
			name: 'Uzbequistão',
		},
		{
			iso: 'VU',
			name: 'Vanuatu',
		},
		{
			iso: 'VE',
			name: 'Venezuela',
		},
		{
			iso: 'VN',
			name: 'Vietnã',
		},
		{
			iso: 'WF',
			name: 'Wallis e Futuna',
		},
		{
			iso: 'ZM',
			name: 'Zâmbia',
		},
		{
			iso: 'ZW',
			name: 'Zimbábue',
		},
	],
}
